import axiosInstance from '../../../fetchConfig/axiosConfig.js';
import { usePersistedDataStore } from '../../store.js';
import { toast } from '../../../common/toast';
import { regenerateWorkupsByEvidenceTypes, regenerateWorkups } from '../../../services/regenerateWorkupsService.js';
import { isCaseSettled } from '../SummaryCaseTable/utils/helpers.js';
import { findNegotiationCaseByParcelId } from '../../../utils/conversionUtils.js';
import { uploadFileToS3Presigned } from '../Offers/s3UploadService.js';
import { scarIndexKey, findScarIndexKeys } from '../../../utils/scarIndex.js';
import logger from '../../../utils/logger';

const REPORT_TYPE_TO_EVIDENCE = {
  standard: 'unequal',
  assessment: 'excessive',
};

const EVIDENCE_TYPE_TO_API = { Unequal: 'unequal', Excessive: 'excessive' };

export const NYSCEF_UPLOAD_METHODS = {
  EVIDENCE: 'evidence',
  MISC: 'misc',
};

export const NYSCEF_DOC_TYPES = {
  EXHIBIT: 'EXHIBIT',
  LETTER: 'LETTER',
};

export const EXHIBIT_LABEL_MODES = {
  NUMBER: 'NUMBER',
  LETTER: 'LETTER',
};

const MISC_S3_BUCKET = 'aventine-court-docs';

/** When true, nothing is regenerated, uploaded to S3, or sent to /upload-evidence; callers get the would-be request back as gatedPayload. */
export const NYSCEF_UPLOAD_GATE = false;

const idMatchKey = (v) =>
  `_${String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}_`;

/** SCAR indexes match in any county format; other IDs (parcel) match with /, -, _, and other punctuation treated the same. */
const nameHasId = (filename, id) => {
  const stem = String(filename || '').replace(/\.[^.]+$/, '');
  const scarKey = scarIndexKey(id);
  if (scarKey) return findScarIndexKeys(stem).has(scarKey);
  const idKey = idMatchKey(id);
  return idKey.length > 2 && idMatchKey(stem).includes(idKey);
};

const settledWhy = (caseItem) => {
  if (!isCaseSettled(caseItem)) return null;
  if (caseItem.BARSettleDate) return `BARSettleDate=${caseItem.BARSettleDate}`;
  if (caseItem.SCARSettleDate) return `SCARSettleDate=${caseItem.SCARSettleDate}`;
  if (caseItem.SCARDeterminationDate) {
    return `SCARDeterminationDate=${caseItem.SCARDeterminationDate} action=${caseItem.SCARDeterminationAction ?? ''}`;
  }
  if (caseItem.BARFiled === 1 && caseItem.SCARFiled === 0 && caseItem.BARDeterminationAction) {
    return `BARDeterminationAction=${caseItem.BARDeterminationAction}`;
  }
  return 'isCaseSettled';
};

const zipPath = (relativePath) => String(relativePath || '').replace(/\\/g, '/');
const zipBasename = (relativePath) => zipPath(relativePath).split('/').filter(Boolean).pop() || '';
const isPdfZipName = (name) => {
  const base = String(name || '');
  return base.toLowerCase().endsWith('.pdf') || !base.includes('.');
};

export const miscZipFilenameExample = (scarID, parcelID) => {
  const scarSlug = String(scarID || '').trim().replace(/[^A-Za-z0-9]+/g, '_');
  if (scarSlug) return `SCAR_${scarSlug}_Rebuttal_Package.pdf`;
  const parcel = String(parcelID || '').trim();
  return parcel ? `${parcel}_Rebuttal_Package.pdf` : 'SCAR_809378_2025_Rebuttal_Package.pdf';
};

const zipNamingHint = (example) =>
  `Name each PDF with that case's SCAR index or parcel ID, e.g. ${example}`;

/** Map PDFs (loose or inside ZIPs) to selected cases by SCAR index or parcel ID in the filename. A lone loose PDF matching no case is filed on every selected case. */
export const mapFilesToSelectedCases = async (files, rows, negotiationObj) => {
  const caseTargets = [];
  for (const row of rows ?? []) {
    const caseItem = findNegotiationCaseByParcelId(negotiationObj, row?.parcel_id ?? row?.id);
    if (!caseItem) continue;
    const scarID = String(caseItem.SCARIndexNumber ?? caseItem.scar_id ?? row?.SCARIndexNumber ?? '').trim();
    const villageScarID = String(caseItem.VillageSCARIndexNumber ?? '').trim();
    const parcelID = String(caseItem.parcel_id ?? row?.parcel_id ?? '').trim();
    if (!parcelID || (!scarID && !villageScarID)) continue;
    caseTargets.push({
      scarID: scarID || villageScarID,
      parcelID,
      matchIDs: [parcelID, scarID, villageScarID].filter(Boolean),
      settled: isCaseSettled(caseItem),
      settledWhy: settledWhy(caseItem),
    });
  }
  if (!caseTargets.length) return { error: 'No cases to upload for.' };
  const example = miscZipFilenameExample(caseTargets[0].scarID, caseTargets[0].parcelID);
  logger.log('[Upload Misc] Case indexes available to match', caseTargets.map((c) => ({
    scarID: c.scarID,
    scarKey: scarIndexKey(c.scarID),
    parcelID: c.parcelID,
    settled: c.settled,
    settledWhy: c.settledWhy,
  })));
  try {
    const entries = [];
    const dropped = [];
    for (const file of files ?? []) {
      if (!String(file?.name || '').toLowerCase().endsWith('.zip')) {
        entries.push({ name: file.name, path: file.name, file });
        continue;
      }
      const jszipModule = await import('jszip');
      const JSZip = jszipModule.default ?? jszipModule;
      const zip = await JSZip.loadAsync(file);
      zip.forEach((relativePath, entry) => {
        if (entry.dir) return;
        const path = zipPath(relativePath);
        const name = zipBasename(path);
        if (!name || name.startsWith('.') || path.toLowerCase().includes('__macosx/')) return;
        if (isPdfZipName(name)) entries.push({ name, path, entry, zipName: file.name });
        else dropped.push({ filename: path, reason: 'non-PDF documents skipped' });
      });
    }
    if (!entries.length && !dropped.length) return { error: 'No files to upload.' };

    const sharedEligible = entries.filter((e) => e.file).length === 1;
    const usedParcels = new Set();
    const mappings = [];
    for (const e of entries) {
      let matches = caseTargets.filter((c) => c.matchIDs.some((id) => nameHasId(e.path, id) || nameHasId(e.name, id)));
      if (!matches.length && e.zipName) {
        matches = caseTargets.filter((c) => c.matchIDs.some((id) => nameHasId(e.zipName, id)));
      }
      const shared = !matches.length && !!e.file && sharedEligible;
      const targets = shared ? caseTargets : matches;
      let error = null;
      if (!targets.length) error = 'not matched to cases';
      else if (!shared && targets.length > 1) error = 'matched multiple cases';
      else if (targets.some((c) => c.settled)) error = shared ? 'blocked from every case (a selected case is settled)' : 'matched settled cases';
      else if (!shared && usedParcels.has(targets[0].parcelID)) error = 'duplicates for a case that already has a file';
      logger.log('[Upload Misc] File match attempt', {
        filename: e.path,
        shared,
        tryingParcelIDs: targets.map((c) => c.parcelID),
        tryingScarIDs: matches.map((c) => c.scarID),
        settledWhy: targets.find((c) => c.settled)?.settledWhy ?? null,
        error,
      });
      if (error) { dropped.push({ filename: e.path, reason: error }); continue; }
      const filename = e.name.toLowerCase().endsWith('.pdf') ? e.name : `${e.name}.pdf`;
      const file = e.file ?? new File([await e.entry.async('blob')], filename, { type: 'application/pdf' });
      for (const c of targets) {
        if (!shared) usedParcels.add(c.parcelID);
        mappings.push({
          file,
          filename,
          scarID: c.scarID,
          parcelID: c.parcelID,
          matchBy: shared ? 'shared' : nameHasId(e.path, c.parcelID) ? 'parcelID' : 'scarID',
        });
      }
    }

    if (!mappings.length) {
      const allNotPdf = dropped.length && dropped.every((d) => d.reason === 'non-PDF documents skipped');
      logger.warn('[Upload Misc] No mappings', { dropped, scarIDs: caseTargets.map((c) => c.scarID) });
      return {
        error: describeDroppedFiles(dropped, example, allNotPdf ? 'No PDFs found' : 'No files could be matched to a selected case'),
        dropped,
        example,
      };
    }

    logger.log('[Upload Misc] Mapped files to selected cases', mappings.map((m) => ({
      filename: m.filename, parcelID: m.parcelID, scarID: m.scarID, matchBy: m.matchBy,
    })));
    if (dropped.length) {
      logger.warn('[Upload Misc] Dropped unmatched files', dropped);
    }
    return { mappings, dropped, example };
  } catch (err) {
    logger.error('[Upload Misc] Failed to read files', err);
    return { error: err?.message || 'Could not read uploaded files.' };
  }
};

/** Counts of files dropped by mapFilesToSelectedCases grouped by reason, e.g. "12 non-PDF documents skipped, 22 not matched to cases". */
export const describeDroppedFiles = (dropped, example, heading = 'Some files were skipped') => {
  const counts = new Map();
  for (const d of dropped ?? []) counts.set(d.reason, (counts.get(d.reason) || 0) + 1);
  const lines = [...counts].map(([reason, n]) => `- ${n} ${reason}`).join('\n');
  return `${heading}:\n${lines}${counts.has('not matched to cases') && example ? `\n\n${zipNamingHint(example)}.` : ''}`;
};

const slugifyForS3 = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 80) || 'document';

const buildMiscS3Key = ({ year, parcelID, scarID, description, fileName, disambiguate = false }) => {
  const scarSlug = String(scarID || '').replace(/\//g, '-');
  const fileSlug = slugifyForS3(String(fileName || '').replace(/\.pdf$/i, ''));
  const descSlug = slugifyForS3(description) || fileSlug;
  const name = disambiguate && fileSlug && fileSlug !== descSlug ? `${descSlug}_${fileSlug}` : descSlug;
  return `residential/evidence/${year}/${parcelID}/${scarSlug}_${name}.pdf`;
};

/**
 * Build documents array for upload-evidence. evidenceTypes per document from case SelectedEvidenceTypes; fallback from override or negotiationObj.reportType when empty.
 * @param {Array} rows - Table rows (selected or all)
 * @param {Object} user - User (userId)
 * @param {Array<string>} [evidenceTypesOverride] - e.g. ['Unequal','Excessive']; used when case has no SelectedEvidenceTypes
 * @returns {Array<Object>} documents with scarID, parcelID, year, negotiatorID, isVillage, evidenceTypes
 */
const buildDocuments = (rows, user, evidenceTypesOverride) => {
  const { negotiationObj } = usePersistedDataStore.getState();
  const year = negotiationObj?.TaxYear ? parseInt(negotiationObj.TaxYear, 10) : new Date().getFullYear();
  const isVillage = negotiationObj?.villageRun === 1;
  logger.log('isVillage', isVillage);
  const negotiatorID = user?.userId ?? undefined;
  const reportType = negotiationObj?.reportType;
  const defaultEvidenceType = REPORT_TYPE_TO_EVIDENCE[reportType];
  const overrideApi = evidenceTypesOverride?.length
    ? evidenceTypesOverride.map((t) => EVIDENCE_TYPE_TO_API[t] ?? t?.toLowerCase()).filter(Boolean)
    : null;
  logger.log('overrideApi', overrideApi);
  if (!rows?.length) {
    logger.warn('[Upload Evidence] buildDocuments: no rows');
    return [];
  }

  return rows.map((row) => {
    const caseItem = findNegotiationCaseByParcelId(negotiationObj, row.parcel_id || row.id);
    if (!caseItem) throw new Error('[Upload Evidence] Missing case object for row.');
    const selected = (caseItem.SelectedEvidenceTypes || []).filter((t) => t && typeof t === 'string');
    const identifiers = overrideApi ?? (selected.length
      ? selected.map((t) => EVIDENCE_TYPE_TO_API[t] ?? t.toLowerCase()).filter(Boolean)
      : [defaultEvidenceType]);
    const scarID = String(caseItem.SCARIndexNumber ?? caseItem.scar_id ?? '').trim();
    const parcelID = String(caseItem.parcel_id ?? '').trim();
    return {
      scarID: scarID || '',
      parcelID: parcelID || '',
      year,
      ...(negotiatorID != null && { negotiatorID }),
      isVillage: !!isVillage,
      identifier: identifiers,
    };
  });
};

/**
 * Upload evidence for NYSCEF. Payload: documents (scarID, parcelID, year, negotiatorID, isVillage), testing.
 * @param {Object} options
 * @param {Array} options.rows - Table rows (selected or all)
 * @param {Object} options.user - User (userId)
 * @param {Array<string>} [options.evidenceTypes] - e.g. ['Unequal','Excessive']; used when case has no SelectedEvidenceTypes
 * @param {boolean} [options.forceOverride] - If true, forces upload when evidence already exists (second-confirm flow)
 * @param {boolean} [options.testing] - Optional override for test mode; in development (REACT_APP_ENV === 'development') testing is forced to true.
 * @returns {void} Does not await response; caller should show confirmation toast after invoking.
 */
export const uploadEvidence = ({ rows, user, evidenceTypes, forceOverride = false, testing = false }) => {
  const envTesting = process.env.REACT_APP_ENV === 'development';
  const effectiveTesting = envTesting || testing;
  if (effectiveTesting) {
    logger.error('IN TESTING MODE WILL NOT UPLOAD EVIDENCE');
  }
  const { setLoadingModal, negotiationObj } = usePersistedDataStore.getState();

  logger.log('[Upload Evidence] Starting', { rowCount: rows?.length, testing: effectiveTesting, forceOverride, reportType: negotiationObj?.reportType, evidenceTypes });

  setLoadingModal({ label: 'Preparing upload...', currentStep: 1, totalSteps: 2 });

  const documents = buildDocuments(rows, user, evidenceTypes);
  logger.log('documents', documents);
  const payload = {
    documents,
    testing: effectiveTesting,
    realFrom: user?.email || 'Propriety',
    forceOverride,
  };

  logger.log('[Upload Evidence] Payload built', { documentCount: documents.length, payloadKeys: Object.keys(payload) });
  logger.log('payload', payload);
  if (NYSCEF_UPLOAD_GATE) {
    setLoadingModal(null);
    return payload;
  }

  setLoadingModal({ label: 'Uploading evidence...', currentStep: 2, totalSteps: 2 });

  logger.log('[Upload Evidence] Payload', payload);
  axiosInstance.post('/upload-evidence', payload).catch((err) => {
    setLoadingModal(null);
    logger.error('[Upload Evidence] Error', err?.response?.data || err?.message);
  });
  setLoadingModal(null);
};

/**
 * Upload PDFs and/or ZIPs to S3 then queue NYSCEF filing via s3Bucket/s3Key.
 * Each PDF (loose or in a ZIP) is filed only on the case matched by filename (SCAR ID or parcel ID); see mapFilesToSelectedCases.
 * @param {Object} options
 * @param {Array} options.rows - Table rows (selected or all)
 * @param {Object} options.user - User (email for realFrom)
 * @param {File[]} options.files - PDF and/or ZIP files
 * @param {Array} [options.mappings] - Precomputed file-to-case mappings from drop validation
 * @param {'EXHIBIT'|'LETTER'} [options.nyscefDocType='EXHIBIT']
 * @param {string} [options.description] - Document description shown below the document type on NYSCEF
 * @param {'NUMBER'|'LETTER'} [options.exhibitLabelMode='NUMBER'] - Exhibit label style (EXHIBIT only)
 * @param {boolean} [options.forceOverride]
 * @param {boolean} [options.testing]
 */
export const uploadMiscellaneousDocument = async ({
  rows,
  user,
  files,
  mappings = null,
  nyscefDocType = NYSCEF_DOC_TYPES.EXHIBIT,
  description = '',
  exhibitLabelMode = EXHIBIT_LABEL_MODES.NUMBER,
  forceOverride = false,
  testing = false,
}) => {
  const envTesting = process.env.REACT_APP_ENV === 'development';
  const effectiveTesting = envTesting || testing;
  const { setLoadingModal, negotiationObj } = usePersistedDataStore.getState();
  const year = negotiationObj?.TaxYear ? parseInt(negotiationObj.TaxYear, 10) : new Date().getFullYear();
  const rowsToUse = rows ?? [];
  const fileList = files ?? [];

  if (!fileList.length) {
    toast.error('Select a PDF or ZIP to upload.');
    return { success: false };
  }
  const invalid = fileList.find((f) => {
    const name = String(f?.name || '').toLowerCase();
    return !name.endsWith('.zip') && !name.endsWith('.pdf') && f?.type !== 'application/pdf';
  });
  if (invalid) {
    toast.error('Only PDF or ZIP files can be filed to NYSCEF.');
    return { success: false };
  }

  const mapped = mappings?.length ? { mappings } : await mapFilesToSelectedCases(fileList, rowsToUse, negotiationObj);
  if (mapped.error) {
    toast.error(mapped.error, { autoClose: false });
    return { success: false };
  }
  if (mapped.dropped?.length) {
    toast.warning(describeDroppedFiles(mapped.dropped, mapped.example), { autoClose: false });
  }
  const uploadTargets = mapped.mappings;

  if (effectiveTesting) {
    logger.error('IN TESTING MODE WILL NOT UPLOAD MISC DOCUMENT');
  }

  try {
    setLoadingModal({ label: 'Uploading PDF to S3...', currentStep: 1, totalSteps: 2 });
    const documents = [];
    const filesPerParcel = new Map();
    for (const t of uploadTargets) filesPerParcel.set(t.parcelID, (filesPerParcel.get(t.parcelID) || 0) + 1);
    const usedKeys = new Set();
    for (let i = 0; i < uploadTargets.length; i += 1) {
      const { scarID, parcelID, file: uploadFile, filename } = uploadTargets[i];
      let s3Key = buildMiscS3Key({
        year,
        parcelID,
        scarID,
        description,
        fileName: filename || uploadFile.name,
        disambiguate: filesPerParcel.get(parcelID) > 1,
      });
      if (usedKeys.has(s3Key)) {
        const stem = s3Key.slice(0, -4);
        let n = 2;
        while (usedKeys.has(`${stem}_${n}.pdf`)) n += 1;
        s3Key = `${stem}_${n}.pdf`;
      }
      usedKeys.add(s3Key);
      if (!NYSCEF_UPLOAD_GATE) {
        setLoadingModal({
          label: `Uploading PDF to S3 (${i + 1}/${uploadTargets.length})...`,
          currentStep: 1,
          totalSteps: 2,
        });
        await uploadFileToS3Presigned(uploadFile, s3Key, MISC_S3_BUCKET, filename || uploadFile.name);
      }
      const doc = {
        parcelID,
        scarID,
        year,
        s3Bucket: MISC_S3_BUCKET,
        s3Key,
        nyscefDocType,
      };
      if (String(description || '').trim()) doc.description = String(description).trim();
      if (nyscefDocType === NYSCEF_DOC_TYPES.EXHIBIT) {
        doc.exhibitLabelMode = exhibitLabelMode || EXHIBIT_LABEL_MODES.NUMBER;
      }
      documents.push(doc);
    }

    setLoadingModal({ label: 'Queuing NYSCEF filing...', currentStep: 2, totalSteps: 2 });
    const payload = {
      documents,
      testing: effectiveTesting,
      realFrom: user?.email || 'Propriety',
      forceOverride: !!forceOverride,
    };
    logger.log('[Upload Misc] Payload', payload);
    if (NYSCEF_UPLOAD_GATE) {
      setLoadingModal(null);
      return {
        success: true,
        gatedPayload: {
          skippedS3Uploads: uploadTargets.map((t, i) => ({ file: t.filename, bucket: MISC_S3_BUCKET, key: documents[i].s3Key })),
          '/upload-evidence': payload,
        },
      };
    }
    axiosInstance.post('/upload-evidence', payload).catch((err) => {
      setLoadingModal(null);
      logger.error('[Upload Misc] Error', err?.response?.data || err?.message);
    });
    setLoadingModal(null);
    if (effectiveTesting) {
      toast.info('Testing mode — NYSCEF will not file this document.', { autoClose: false });
    } else {
      toast.success('NYSCEF upload started — check Humphrey for completion confirmation.', { autoClose: false });
    }
    return { success: true };
  } catch (err) {
    setLoadingModal(null);
    logger.error('[Upload Misc] Failed', err);
    toast.error(err?.message || 'Miscellaneous document upload failed.');
    return { success: false };
  }
};

/**
 * Regenerate workups for the given cases then upload evidence to NYSCEF. Uses selectedRows when provided, else rows.
 * @param {Object} options
 * @param {Array} [options.selectedRows] - Selected table rows
 * @param {Array} options.rows - All table rows (fallback when no selection)
 * @param {Object} options.user - User (userId)
 * @param {Array<string>} [options.evidenceTypes] - Evidence types selected to upload (e.g. ['Unequal', 'Excessive']); mapped to workup report types internally; fallback from negotiationObj.reportType when empty
 * @param {boolean} [options.forceOverride] - If true, forces upload when evidence already exists (second-confirm flow)
 */
export const triggerWorkupRegenerationAndUpload = async ({ selectedRows, rows, user, evidenceTypes, forceOverride = false }) => {
  const { setLoadingModal, negotiationObj: getNegotiationObj } = usePersistedDataStore.getState();
  const rowsToUse = selectedRows?.length ? selectedRows : rows;
  const parcelIds = rowsToUse?.map((r) => r.parcel_id).filter(Boolean) ?? [];

  if (!parcelIds.length) {
    toast.error('No cases to upload evidence for.');
    return;
  }

  // Extra guard, should really not be necessary at all.
  const casesByParcelId = new Map((getNegotiationObj?.cases ?? []).map((c) => [c?.parcel_id, c]).filter(([parcelId]) => !!parcelId));
  const settledCount = rowsToUse.reduce((count, row) => {
    const caseItem = casesByParcelId.get(row?.parcel_id);
    if (!caseItem) throw new Error('[Upload Evidence] Missing case object for settled check.');
    return isCaseSettled(caseItem) ? count + 1 : count;
  }, 0);
  if (settledCount > 0) {
    toast.error(`Upload prevented: ${settledCount} selected ${settledCount === 1 ? 'case is' : 'cases are'} settled.`);
    return;
  }

  const reportType = getNegotiationObj?.reportType || 'standard';
  const useEvidenceTypes = evidenceTypes?.length ? evidenceTypes : null;
  const regenReportTypes = reportType === 'standard' || reportType === 'village' ? ['fnmaSummary'] : ['onepagermaps'];
  const uploadArgs = { rows: rowsToUse, user, evidenceTypes: useEvidenceTypes ?? undefined, forceOverride: !!forceOverride };
  if (NYSCEF_UPLOAD_GATE) {
    return {
      gatedPayload: {
        skippedWorkupRegeneration: { parcelIds, ...(useEvidenceTypes ? { evidenceTypes: useEvidenceTypes } : { reportTypes: regenReportTypes }) },
        '/upload-evidence': uploadEvidence(uploadArgs),
      },
    };
  }
  const regenInvocation = useEvidenceTypes
    ? regenerateWorkupsByEvidenceTypes({ parcelIds, evidenceTypes: useEvidenceTypes, userId: user?.userId })
    : regenerateWorkups({ parcelIds, userId: user?.userId, reportTypes: regenReportTypes });
  logger.log('[Upload Evidence] Dialog confirmed', { rowCount: rowsToUse?.length, evidenceTypes: useEvidenceTypes });
  try {
    setLoadingModal({ label: 'Regenerating documents...', currentStep: 1, totalSteps: 2 });
    const regenResult = await regenInvocation;

    // AFTER REGENERATION, we upload the evidence.
    const regenErrors = Array.isArray(regenResult?.errors) ? regenResult.errors : [];
    if (!regenResult?.success || regenErrors.length > 0) {
      logger.error('[Upload Evidence] Regeneration failed; upload will not start.', {
        success: regenResult?.success,
        errorCount: regenErrors.length,
        errors: regenErrors,
      });
      // toast.error(regenResult?.message || 'Workup regeneration failed');
      setLoadingModal(null);
      return;
    }
    setLoadingModal({ label: 'Uploading evidence...', currentStep: 2, totalSteps: 2 });
    uploadEvidence(uploadArgs);
    setLoadingModal(null);
    toast.success('Nyscef Upload started, check your messages from Humphrey for a completion confirmation.', { autoClose: false });
  } catch (err) {
    toast.error(err?.message || 'Upload evidence failed.');
    setLoadingModal(null);
  }
};
